import { Box, Skeleton, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useCriteriaSectionView } from '@verantos-dev/app-state-cohort-builder';

import {
  CohortBuilderInfoCard,
  type CohortBuilderInfoItem,
} from '../../../../components/CohortBuilderInfoCard/CohortBuilderInfoCard';
import { CohortSection } from '../../../../components/CohortSection/CohortSection';
import { useCohortBuilderModalContext } from '../../../../contexts/CohortBuilderModalContext';
import { useEmitResearchAssistantUpdate } from '../../../../hooks/common/useResearchAssistantUpdateForStudyPeriodCard';
import { SubjectVariableEditCard } from '../../cohort-builder-edit-cards/SubjectVariableEditCard/SubjectVariableEditCard';

export const CriteriaSection = () => {
  const intl = useIntl();
  const theme = useTheme();

  const {
    isLoading,
    subjectVariables,
    showSubjectVariableEditModal,
    modal,
    viewMode,

    onEditClick,
    onDeleteClick,
    onAddSubjectVariableClick,
  } = useCriteriaSectionView();
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate(viewMode);
  const { activeDialog } = useCohortBuilderModalContext();
  const isAddSubjectVariableButtonDisabled = modal !== undefined || activeDialog !== null;

  const headerProps = {
    title: intl.formatMessage({
      id: 'projects.cohortBuilderPage.criteriaSection.title',
    }),
    action: !viewMode
      ? {
          label: `${intl.formatMessage({
            id: 'projects.cohortBuilderPage.subjectVariablesSection.buttonLabel',
          })}`,
          labelIcon: 'add',
          onClick: onAddSubjectVariableClick,
          disabled: isAddSubjectVariableButtonDisabled,
        }
      : undefined,
  };

  const emptyStateProps = {
    id: 'no-definition-variables',
    mainMessage: intl.formatMessage({
      id: 'projects.cohortBuilderPage.criteriaSection.noDefinitionVariablesMainMsg',
    }),
    subMessage: intl.formatMessage({
      id: 'projects.cohortBuilderPage.criteriaSection.noDefinitionVariablesSubMsg',
    }),
  };

  if (isLoading) {
    return (
      <CohortSection headerProps={headerProps} emptyStateProps={emptyStateProps}>
        <Skeleton variant="rectangular" height={78} />
        <Skeleton variant="rectangular" height={78} />
      </CohortSection>
    );
  }

  return (
    <>
      <CohortSection
        key="criteria-section"
        headerProps={headerProps}
        emptyStateProps={emptyStateProps}
      >
        {subjectVariables && subjectVariables.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(6) }}>
            {subjectVariables.map(({ id, name, type, criteriaSelection }) => {
              const subjectInfo: CohortBuilderInfoItem[] = [
                { label: 'Name', value: name, flex: 2 },
                { label: 'Type', value: type },
                { label: 'I/E criteria', value: criteriaSelection },
              ];

              return (
                <CohortBuilderInfoCard
                  key={id}
                  id={id}
                  info={subjectInfo}
                  onEditClick={onEditClick}
                  onDeleteClick={onDeleteClick(emitResearchAssistantUpdate)}
                  viewMode={viewMode}
                  deleteTooltip={intl.formatMessage({
                    id: 'projects.cohortBuilder.cohortBuilderItemViewCard.deleteNotInUse',
                  })}
                />
              );
            })}
          </Box>
        )}
      </CohortSection>

      {showSubjectVariableEditModal && <SubjectVariableEditCard />}
    </>
  );
};
